/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState } from 'react';
import { 
  Code2, 
  Copy, 
  Check, 
  Sparkles, 
  ExternalLink, 
  ShieldCheck, 
  Smartphone, 
  Zap, 
  Settings2,
  CheckCircle2
} from 'lucide-react';

interface EmbedCalloutBannerProps {
  calcId: string;
  calcName: string;
  calcPath: string;
  defaultHeight?: number;
  navigateTo?: (path: string) => void;
}

export function EmbedCalloutBanner({
  calcId,
  calcName,
  calcPath,
  defaultHeight = 880,
  navigateTo
}: EmbedCalloutBannerProps) {
  const [copied, setCopied] = useState<boolean>(false);

  // Standard clean iframe snippet for 1-click quick embedding
  const quickEmbedSnippet = `<!-- Smart-Rechnr.de: ${calcName} -->
<iframe
  src="https://smart-rechnr.de${calcPath}?embed=true"
  title="${calcName} – smart-rechnr.de"
  width="100%"
  height="${defaultHeight}px"
  style="border: 1px solid #e2e8f0; border-radius: 16px; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.08); display: block;"
  frameborder="0"
  scrolling="auto"
  loading="lazy"
  allow="clipboard-write"
></iframe>
<div style="font-size: 11px; color: #64748b; text-align: right; padding: 6px 4px 0 0;">
  Kostenloser Rechner von <a href="https://smart-rechnr.de${calcPath}" target="_blank" rel="noopener" style="color: #004a99; font-weight: 600; text-decoration: none;">smart-rechnr.de</a>
</div>`;

  const handleCopy = () => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(quickEmbedSnippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  const handleOpenGenerator = () => {
    const targetUrl = `/rechner-einbinden?calc=${calcId}`;
    if (navigateTo) {
      navigateTo(targetUrl);
    } else if (typeof window !== 'undefined') {
      window.location.href = targetUrl;
    }
  };

  return (
    <section 
      aria-label="Rechner in eigene Website einbinden"
      className="relative overflow-hidden bg-gradient-to-br from-[#003870]/5 via-blue-50/70 to-indigo-50/60 rounded-3xl border-2 border-blue-200/90 p-6 sm:p-8 lg:p-10 shadow-sm transition hover:shadow-md text-left my-8"
      id="embed-generator-section"
    >
      {/* Decorative background circle */}
      <div className="absolute top-0 right-0 w-72 h-72 bg-gradient-to-br from-blue-400/10 to-indigo-400/10 rounded-full blur-2xl pointer-events-none -mr-16 -mt-16"></div>

      <div className="relative z-10 space-y-6">
        
        {/* Top Badges & Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-blue-200/60 pb-5">
          <div className="space-y-1.5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#004a99] text-white text-[11px] font-bold uppercase tracking-wider shadow-2xs">
              <Code2 className="w-3.5 h-3.5 text-blue-200" />
              <span>HTML Embed-Code &bull; Kostenlos für Webmaster &amp; Blogs</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 tracking-tight font-display">
              Möchten Sie diesen Rechner in Ihre eigene Website einbinden?
            </h2>
            <p className="text-xs sm:text-sm text-slate-600 max-w-3xl leading-relaxed">
              Bieten Sie Ihren Lesern einen echten Mehrwert! Integrieren Sie den <strong>{calcName}</strong> mit wenigen Klicks auf Ihrer Website, Ihrem WordPress-Blog oder in Redaktionsartikel &ndash; <strong>100 % kostenlos, werbefrei und DSGVO-konform</strong>.
            </p>
          </div>

          <div className="shrink-0 flex items-center">
            <button
              type="button"
              onClick={handleOpenGenerator}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-gradient-to-r from-[#004a99] to-blue-700 hover:from-[#003d7e] hover:to-blue-800 text-white font-bold text-xs sm:text-sm shadow-md hover:shadow-lg transition cursor-pointer group"
            >
              <Settings2 className="w-4 h-4 group-hover:rotate-45 transition-transform" />
              <span>Code anpassen &amp; generieren</span>
              <ExternalLink className="w-3.5 h-3.5 opacity-80" />
            </button>
          </div>
        </div>

        {/* Feature Pills */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 text-xs">
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/80 border border-blue-100 shadow-2xs text-slate-700">
            <Zap className="w-4 h-4 text-amber-500 shrink-0" />
            <span className="font-semibold text-[11px] sm:text-xs">100 % Werbefrei</span>
          </div>
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/80 border border-blue-100 shadow-2xs text-slate-700">
            <Smartphone className="w-4 h-4 text-[#004a99] shrink-0" />
            <span className="font-semibold text-[11px] sm:text-xs">Voll Responsive</span>
          </div>
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/80 border border-blue-100 shadow-2xs text-slate-700">
            <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
            <span className="font-semibold text-[11px] sm:text-xs">DSGVO-konform</span>
          </div>
          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-white/80 border border-blue-100 shadow-2xs text-slate-700">
            <Sparkles className="w-4 h-4 text-indigo-600 shrink-0" />
            <span className="font-semibold text-[11px] sm:text-xs">Live-Daten 2027</span>
          </div>
        </div>

        {/* Quick Embed Snippet Box */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-bold text-slate-700 flex items-center gap-1.5">
              <span>Fertiger HTML-Iframe Code (Sofort einsatzbereit):</span>
            </span>
            <span className="text-[11px] text-slate-500 font-medium hidden sm:inline">
              Einfach in HTML, WordPress, Typo3, Wix oder Webflow einfügen
            </span>
          </div>

          <div className="relative group">
            <pre className="w-full bg-slate-900 text-blue-100 font-mono text-[11px] sm:text-xs p-4 sm:p-5 rounded-2xl overflow-x-auto border border-slate-800 shadow-inner leading-relaxed">
              <code>{quickEmbedSnippet}</code>
            </pre>

            <div className="absolute top-3 right-3 flex items-center gap-2">
              <button
                type="button"
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold text-xs transition cursor-pointer shadow-sm ${
                  copied
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white hover:bg-slate-100 text-slate-800 border border-slate-200'
                }`}
                title="Embed-Code in die Zwischenablage kopieren"
              >
                {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5 text-slate-500" />}
                <span>{copied ? 'Code kopiert!' : 'Code kopieren'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Footer Guidance Link to Full Generator Page */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs text-slate-500 pt-2 border-t border-blue-200/50 gap-2">
          <div className="flex items-center gap-1.5">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <span>Benötigen Sie WordPress Gutenberg-Blocks, Elementor Widgets oder individuelle Breiten?</span>
          </div>
          <button
            type="button"
            onClick={handleOpenGenerator}
            className="text-[#004a99] hover:text-blue-800 font-bold hover:underline inline-flex items-center gap-1 cursor-pointer"
          >
            <span>Hier im offiziellen Widget-Generator konfigurieren</span>
            <ExternalLink className="w-3.5 h-3.5" />
          </button>
        </div>

      </div>
    </section>
  );
}
